import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CookieConsent } from "@/components/CookieConsent";
import { AuthProvider } from "@/hooks/useAuth";

// Home page loaded eagerly for fast initial paint
import Home from "./pages/Home";

// Lazy load all other pages to reduce initial bundle size

const PricingPage = lazy(() => import("./pages/PricingPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostUSALI = lazy(() => import("./pages/BlogPostUSALI"));
const BlogPostTechMap = lazy(() => import("./pages/BlogPostTechMap"));

const ApplicationPage = lazy(() => import("./pages/ApplicationPage"));
const CulturePage = lazy(() => import("./pages/CulturePage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const DashboardLayout = lazy(() => import("./pages/dashboard/DashboardLayout"));
const CommandCenter = lazy(() => import("./pages/dashboard/CommandCenter"));
const AdminConsole = lazy(() => import("./pages/dashboard/AdminConsole"));

// Solution Pages - lazy loaded
const HotelsSolutionPage = lazy(() => import("./pages/solutions/HotelsSolutionPage"));
const ServicedApartmentsSolutionPage = lazy(() => import("./pages/solutions/ServicedApartmentsSolutionPage"));
const CampsiteSolutionPage = lazy(() => import("./pages/solutions/CampsiteSolutionPage"));
const RestaurantsCafesSolutionPage = lazy(() => import("./pages/solutions/RestaurantsCafesSolutionPage"));
const BarsNightlifeSolutionPage = lazy(() => import("./pages/solutions/BarsNightlifeSolutionPage"));
const FitnessAcademiesSolutionPage = lazy(() => import("./pages/solutions/FitnessAcademiesSolutionPage"));
const SpaWellnessSolutionPage = lazy(() => import("./pages/solutions/SpaWellnessSolutionPage"));
const GolfSolutionPage = lazy(() => import("./pages/solutions/GolfSolutionPage"));

// Resource Pages - lazy loaded
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<CommandCenter />} />
                <Route path="admin" element={<AdminConsole />} />
              </Route>
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/usali-blind-spot" element={<BlogPostUSALI />} />
              <Route path="/blog/hospitality-tech-map" element={<BlogPostTechMap />} />
              <Route path="/apply" element={<ApplicationPage />} />
              <Route path="/culture" element={<CulturePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/legal/cookies" element={<CookiesPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/faq" element={<FAQPage />} />
              
              {/* Solution Routes */}
              <Route path="/solutions/hotels" element={<HotelsSolutionPage />} />
              <Route path="/solutions/serviced-apartments-rentals" element={<ServicedApartmentsSolutionPage />} />
              <Route path="/solutions/campsite" element={<CampsiteSolutionPage />} />
              <Route path="/solutions/restaurants-cafes" element={<RestaurantsCafesSolutionPage />} />
              <Route path="/solutions/bars-nightlife" element={<BarsNightlifeSolutionPage />} />
              <Route path="/solutions/fitness-academies" element={<FitnessAcademiesSolutionPage />} />
              <Route path="/solutions/spa-wellness" element={<SpaWellnessSolutionPage />} />
              <Route path="/solutions/golf" element={<GolfSolutionPage />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
